/**
 * useServerPagination
 * -------------------
 * Centralises the state and handlers every backend-paginated list needs, so
 * pages don't re-implement (and subtly diverge on) page / per-page / search
 * plumbing. It is the single source of truth for how the app talks to a
 * paginated API and how it drives the shared `CustomTable`.
 *
 * Typical usage:
 *
 *   const pagination = useServerPagination();
 *   const { data, totalCount, isPending } = useGetSomething(
 *     fleetId,
 *     pagination.page,       // 1-based, ready for the API
 *     pagination.perPage,
 *     pagination.search
 *   );
 *
 *   <CustomTable
 *     ...
 *     isFilterFromBackend
 *     data={data}
 *     tablePagnationCount={totalCount}
 *     {...pagination.tableProps}
 *   />
 *
 * The `search` value is debounced, so binding it directly to the query key is
 * safe: the API only refetches once the user stops typing.
 */
import { useCallback, useMemo, useRef, useState } from "react";

/** Default page size used across every list unless a page overrides it. */
export const DEFAULT_ROWS_PER_PAGE = 25;

/** The page-size choices offered in the table footer. */
export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export interface ServerPaginationOptions {
  /** Initial page size. Defaults to DEFAULT_ROWS_PER_PAGE. */
  initialPerPage?: number;
  /** Debounce (ms) applied to search input before it hits the API. */
  searchDebounceMs?: number;
}

export interface ServerPaginationTableProps {
  /** 0-based page index expected by CustomTable / MUI. */
  pageProps: number;
  rowsPerPageProps: number;
  handleChangePageProps: (zeroBasedPage: number) => void;
  handleChangeRowsPerPageProps: (newPerPage: number) => void;
  resetPaginationTrigger: boolean;
  onChangeSearchTerm: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

export interface ServerPagination {
  /** 1-based current page, ready to pass straight to the API. */
  page: number;
  /** Current page size. */
  perPage: number;
  /** Debounced search term, ready for the API/query key. */
  search: string;
  /** Jump to a specific 1-based page. */
  setPage: (oneBasedPage: number) => void;
  /** Change the page size and return to page 1. */
  setPerPage: (newPerPage: number) => void;
  /** Reset page (and optionally search) back to the start. */
  reset: (opts?: { clearSearch?: boolean }) => void;
  /**
   * Call this after ANY filter change that alters the result set, so the user
   * isn't stranded on an out-of-range page.
   */
  resetToFirstPage: () => void;
  /** Spread straight onto <CustomTable /> for a backend-paginated list. */
  tableProps: ServerPaginationTableProps;
}

export const useServerPagination = (
  options: ServerPaginationOptions = {}
): ServerPagination => {
  const {
    initialPerPage = DEFAULT_ROWS_PER_PAGE,
    searchDebounceMs = 500,
  } = options;

  // 1-based page for the API; CustomTable receives the 0-based equivalent.
  const [page, setPageState] = useState(1);
  const [perPage, setPerPageState] = useState(initialPerPage);
  const [search, setSearch] = useState("");
  // Toggled to tell CustomTable to reset its internal paging state.
  const [resetTrigger, setResetTrigger] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setPage = useCallback((oneBasedPage: number) => {
    setPageState(Math.max(1, oneBasedPage));
  }, []);

  const setPerPage = useCallback((newPerPage: number) => {
    setPerPageState(newPerPage);
    // A larger/smaller page invalidates the current offset.
    setPageState(1);
  }, []);

  const resetToFirstPage = useCallback(() => {
    setPageState(1);
    setResetTrigger((v) => !v);
  }, []);

  const reset = useCallback(
    (opts?: { clearSearch?: boolean }) => {
      setPageState(1);
      setPerPageState(initialPerPage);
      if (opts?.clearSearch) setSearch("");
      setResetTrigger((v) => !v);
    },
    [initialPerPage]
  );

  const onChangeSearchTerm = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSearch(value);
        // New search term → always restart from the first page.
        setPageState(1);
        setResetTrigger((v) => !v);
      }, searchDebounceMs);
    },
    [searchDebounceMs]
  );

  const tableProps = useMemo<ServerPaginationTableProps>(
    () => ({
      pageProps: page - 1,
      rowsPerPageProps: perPage,
      handleChangePageProps: (zeroBasedPage: number) =>
        setPageState(zeroBasedPage + 1),
      handleChangeRowsPerPageProps: setPerPage,
      resetPaginationTrigger: resetTrigger,
      onChangeSearchTerm,
    }),
    [page, perPage, resetTrigger, onChangeSearchTerm, setPerPage]
  );

  return {
    page,
    perPage,
    search,
    setPage,
    setPerPage,
    reset,
    resetToFirstPage,
    tableProps,
  };
};

export default useServerPagination;
